import React, { useCallback, useEffect, useRef } from "react"
import { Box, BoxProps, useTheme } from "@mui/material"
import { deepmerge } from "@mui/utils"
import qs from "qs"
import clsx from "clsx"
import { useNavigate } from "react-router-dom"

import { Carousel, CarouselItem } from "components/Carousel"
import { IExtendOptions } from "components/Carousel/Carousel"
import { IMenuConfig } from "providers/ui/hooks/useAppNavigation/utils"

export interface ICarouselNavigationMenuProps extends IMenuConfig {
  selected?: boolean
}

export interface ICarouselNavigationProps
  extends Pick<BoxProps, "sx" | "className"> {
  navigations: IMenuConfig[]
  value: string
  carouselOptions?: IExtendOptions
  MenuItem: React.FC<ICarouselNavigationMenuProps>
  translations?: string
}

export const CarouselNavigation: React.FC<ICarouselNavigationProps> = ({
  value,
  navigations = [],
  carouselOptions: carouselOptionsProp = {},
  MenuItem,
  translations,
  sx = {},
  ...props
}) => {
  const splideRef = useRef(null)
  const navigate = useNavigate()

  const theme = useTheme()
  const { root, carousel, carouselItem } =
    theme.components[`CarouselNavigation`]?.styleOverrides || {}
  const { carouselOption = {} } =
    theme.components[`CarouselNavigation`]?.defaultProps || {}

  const handleOnReady = useCallback((splide) => {
    const intialSlideElem = splide.root.querySelector(".is-initial")
    if (!intialSlideElem) return
    const [activeSlideIndex] = intialSlideElem
      .getAttribute("aria-label")
      ?.split(" ")
    const slideIndex = +activeSlideIndex - 1
    //Move to current ordinal slide
    splide.go(slideIndex)
    splide.Components.Slides.forEach((item) => {
      item.slide.classList.remove("is-move")
    }, false)
    splide.refresh()
  }, [])

  const handleClickSlide = useCallback((splide, { index }, _event) => {
    splide.go(index)
  }, [])

  const handleOnMoved = useCallback(
    (splide, index, prev, dest) => {
      let path = null
      splide.Components.Slides.forEach((item) => {
        item.slide.classList.remove("is-move")
        if (item.index === index) path = item.slide?.getAttribute("link")
      }, false)

      if (!path) return

      splide.refresh()

      // Prevent unexpected redirect from sub-path to root by checking the current path before deciding to navigate.

      const shouldNavigate = !window.location.pathname.startsWith(
        path.replace("?", "")
      )
      if (!shouldNavigate) return
      navigate(path)
    },
    [navigate]
  )

  const handleOnMove = useCallback((splide) => {
    splide.Components.Slides.forEach((item) => {
      item.slide.classList.add("is-move")
    }, false)
  }, [])

  // Sync Splide slide with active menu by index when menu is activated programmatically.
  useEffect(() => {
    const splide = splideRef.current?.splide
    if (!splide) return
    const currentSlideIndex = splide.index
    const slides = splide?.Components.Slides.get(true)
    const currenMenuIndex = slides.find(
      (item) => item.slide?.getAttribute("path") === value
    )?.index
    if (currentSlideIndex !== currenMenuIndex) {
      splide.go(currenMenuIndex)
    }
  }, [splideRef, value])

  // Ensure translations work in carousel by refreshing Splide.js so cloned slides display correct text.
  useEffect(() => {
    const splide = splideRef.current?.splide
    if (!splide) return
    splide.refresh()
  }, [splideRef, translations])

  const direction = theme.direction

  return (
    <Box sx={deepmerge(root, sx)} {...props}>
      <Carousel
        splideRef={splideRef}
        onClick={handleClickSlide}
        onMoved={handleOnMoved}
        onReady={handleOnReady}
        onMove={handleOnMove}
        options={{
          gap: 0,
          padding: { top: 0, bottom: 0, left: 0, right: 0 },
          autoWidth: true,
          type: "loop",
          drag: false,
          pagination: false,
          autoplay: false,
          perPage: 7,
          focus: "center",
          cloneStatus: false,
          direction: direction,
          ...carouselOption,
          ...carouselOptionsProp,
        }}
        sx={carousel}
      >
        {Array.isArray(navigations) &&
          MenuItem &&
          navigations.map(
            (
              { disabled = false, to, qs: queryString, ...restMenuProps },
              index
            ) => {
              const link = `${to}?${qs.stringify(queryString)}`
              const isMatched = to === value
              const testId = link
                .replace(/\//g, "-")
                .replace(/\?/g, "")
                .replace(/=/g, "-")
              return (
                <CarouselItem
                  className={clsx(
                    "CarouselNavigation-carouselItem",
                    disabled ? "is-disabled" : "",
                    isMatched ? "is-initial" : ""
                  )}
                  data-testid={`nav${testId}`}
                  path={to}
                  link={link}
                  key={to}
                  sx={carouselItem}
                >
                  <MenuItem
                    to={to}
                    qs={queryString}
                    selected={isMatched}
                    {...restMenuProps}
                  />
                </CarouselItem>
              )
            }
          )}
      </Carousel>
    </Box>
  )
}
